import React from 'react';
import { Box } from '@shared/ui/box';
import styled from '@emotion/styled';
import { prefixCls } from '@shared/theme';
import { Skeleton, Typography } from 'antd';
import { Title } from '@shared/ui/typography';
import { Builder } from '@shared/types/builder';
import { RiseOutlined } from '@ant-design/icons';
import { fontVariants } from '@shared/constants/fontVariants';
import { ConnectedInstagram } from '@features/connect-instagram';
import { ComponentNames } from '@shared/constants/componentNames';
import { builderCardSizes } from '@shared/constants/builderCardSizes';

const list: Record<string, boolean> = {
  [ComponentNames.instagram_followers]: true,
  [ComponentNames.instagram_avg_likes]: true,
  [ComponentNames.instagram_avg_comments]: true,
  [ComponentNames.instagram_engagement_rate]: true,
};

const typographyStyle: React.CSSProperties = {
  left: 16,
  bottom: 12,
  fontSize: 12,
  paddingRight: 24,
  position: 'absolute',
};

export const MKitCard = ({
  data,
  icon,
  user,
  value,
  title,
  actions,
  dataGrid,
  activeItem,
  activeSize,
  dataServer,
  showSkeleton,
}: Builder.GridComponentProps & {
  title?: string;
  value?: string;
  activeSize: any;
  activeItem?: string;
  showSkeleton?: boolean;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  allowedSizes: builderCardSizes[];
}) => {
  const isActive = activeItem === dataGrid.i;

  const showMessage =
    !dataServer &&
    !showSkeleton &&
    list[data.name] &&
    value?.startsWith('0') &&
    !!user?.linked_accounts?.instagram;

  const notConnected =
    !dataServer &&
    !showSkeleton &&
    list[data.name] &&
    !user?.linked_accounts?.instagram;

  return (
    <MKitCardContainer
      key={dataGrid.i}
      data-grid={dataGrid}
      className={isActive ? 'active-item' : ''}
    >
      {notConnected && (
        <NotConnected
          isActive={isActive}
          activeSize={activeSize}
          title={title}
        />
      )}
      <>
        {showMessage && (
          <Typography.Text ellipsis type="secondary" style={typographyStyle}>
            Your data will be displayed within{' '}
            <Typography.Text strong type="secondary" style={{ fontSize: 12 }}>
              1h.
            </Typography.Text>
          </Typography.Text>
        )}
        <MKitCardHead>
          <MKitCardHeadSkeleton
            active
            paragraph={false}
            showSkeleton={showSkeleton}
          />

          <CardTitle size={activeSize} showSkeleton={showSkeleton}>
            {icon}
            <Typography.Text className={fontVariants.opt3}>
              {title}
            </Typography.Text>
          </CardTitle>
        </MKitCardHead>

        <MKitCardBody>
          {showSkeleton ? (
            <MKitCardBodySkeleton
              active
              paragraph={false}
              showSkeleton={showSkeleton}
            />
          ) : (
            <MKitCardMeta size={activeSize} showSkeleton={showSkeleton}>
              <Typography.Title
                ellipsis
                level={1}
                className={fontVariants.opt2}
              >
                {value}
              </Typography.Title>

              <Trends trends={data?.trends || 0} activeSize={activeSize} />
            </MKitCardMeta>
          )}
        </MKitCardBody>
      </>
      {!!actions && actions}
    </MKitCardContainer>
  );
};

const MKitCardContainer = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  padding: '1.6rem',
  borderRadius: '2.8rem',
  flexDirection: 'column',
}));

const MKitCardHead = styled('div')(() => ({
  gap: '.4rem',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
}));

const MKitCardBody = styled('div')(() => ({
  top: 0,
  left: 0,
  flexGrow: 1,
  gap: '.8rem',
  width: '100%',
  height: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  paddingTop: '1.6rem',
  paddingInline: '1.6rem',
}));

const MKitCardMeta = styled('div', {
  shouldForwardProp: (prop) => !['size', 'showSkeleton'].includes(prop),
})(
  ({
    size,
    showSkeleton,
  }: {
    size: builderCardSizes;
    showSkeleton?: boolean;
  }) => ({
    gap: '.8rem',
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'baseline',
    opacity: showSkeleton ? 0 : 1,
    [`& h1.${prefixCls}-typography`]: {
      marginBottom: 0,
      fontSize: builderCardSizes.s12 === size ? '3.5rem' : '5.5rem',
    },
  }),
);

const CardTitle = styled('div', {
  shouldForwardProp: (prop) => !['size', 'showSkeleton'].includes(prop),
})(
  ({
    size,
    showSkeleton,
  }: {
    showSkeleton?: boolean;
    size: builderCardSizes;
  }) => ({
    gap: '.4rem',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    opacity: showSkeleton ? 0 : 1,
    [`& > span.${prefixCls}-typography`]: {
      marginBottom: 0,
      whiteSpace: 'nowrap',
      fontSize: [builderCardSizes.s12, builderCardSizes.s14].includes(size)
        ? '1.2rem'
        : '1.6rem',
    },
  }),
);

const MKitCardHeadSkeleton = styled(Skeleton, {
  shouldForwardProp: (prop) => !['showSkeleton'].includes(prop),
})<{ showSkeleton?: boolean }>(({ showSkeleton }) => ({
  display: showSkeleton ? 'table' : 'none',
  [`& .${prefixCls}-skeleton-content .${prefixCls}-skeleton-title`]: {
    width: 111,
    height: 24,
    marginBottom: 0,
  },
}));

const MKitCardBodySkeleton = styled(Skeleton, {
  shouldForwardProp: (prop) => !['showSkeleton'].includes(prop),
})<{ showSkeleton?: boolean }>(({ showSkeleton }) => ({
  display: showSkeleton ? 'table' : 'none',
  [`& .${prefixCls}-skeleton-content .${prefixCls}-skeleton-title`]: {
    height: 46,
    maxWidth: 270,
    marginBottom: 0,
  },
}));

const Trends = ({
  trends,
  activeSize,
}: {
  trends?: number;
  activeSize: builderCardSizes;
}) => {
  let fixed = parseFloat(((trends || 0) * 100).toFixed(1));

  return !!trends &&
    fixed > 0.1 &&
    trends >= 0 &&
    activeSize !== builderCardSizes.s12 ? (
    <Title
      level={3}
      type="success"
      className={fontVariants.opt2}
      titleProps={{ margin: 0, whiteSpace: 'nowrap' }}
    >
      <Box
        boxStyles={{
          gap: '.4rem',
          width: 'unset',
          display: 'inline-flex',
        }}
      >
        <RiseOutlined />
        {(trends * 100).toFixed(1)}%
      </Box>
    </Title>
  ) : null;
};

const NotConnected: React.FC<{
  title?: string;
  activeSize: any;
  isActive: boolean;
}> = ({ title, isActive, activeSize }) => {
  const isSmall = activeSize === '12';

  let styles = {
    width: 'calc(100% + 8px)',
    height: 'calc(100% + 8px)',
    minWidth: 'calc(100% + 8px)',
    minHeight: 'calc(100% + 8px)',
  };

  if (isActive) {
    styles = {
      width: '100%',
      height: '100%',
      minWidth: '100%',
      minHeight: '100%',
    };
  }

  return (
    <div
      style={{
        gap: 24,
        zIndex: 9,
        display: 'flex',
        position: 'absolute',
        alignItems: 'center',
        flexDirection: 'column',
        top: isActive ? 0 : -4,
        left: isActive ? 0 : -4,
        justifyContent: 'center',
        backdropFilter: 'blur(10.5px)',
        borderRadius: isActive ? 20 : 22,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        ...styles,
      }}
    >
      <div
        style={{
          gap: 10,
          padding: 16,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div
          style={{
            gap: 8,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <svg
            width="16"
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="white"
              d="M13.2305 3.7298C13.2305 3.19805 12.7995 2.76868 12.2697 2.76868C11.7399 2.76868 11.3086 3.19805 11.3086 3.7298C11.3086 4.2596 11.7399 4.68896 12.2697 4.68896C12.7995 4.68896 13.2305 4.2596 13.2305 3.7298Z"
            />
            <path
              fill="white"
              d="M14.511 11.2323C14.4755 12.0124 14.345 12.4362 14.2367 12.7177C14.0912 13.0909 13.9176 13.3578 13.6361 13.6377C13.3578 13.9176 13.0909 14.0908 12.7177 14.2347C12.4362 14.3446 12.0108 14.4755 11.2307 14.5126C10.3874 14.5498 10.1375 14.5577 7.99901 14.5577C5.86247 14.5577 5.6106 14.5498 4.76729 14.5126C3.98726 14.4755 3.56382 14.3446 3.2823 14.2347C2.90711 14.0908 2.64223 13.9176 2.36231 13.6377C2.08041 13.3578 1.90685 13.0909 1.76333 12.7177C1.65498 12.4362 1.52256 12.0124 1.48893 11.2323C1.44783 10.389 1.4403 10.1352 1.4403 8.00099C1.4403 5.86247 1.44783 5.6106 1.48893 4.76729C1.52256 3.98725 1.65498 3.56382 1.76333 3.27993C1.90685 2.90711 2.08041 2.64181 2.36231 2.36189C2.64223 2.08239 2.90711 1.90881 3.2823 1.76333C3.56382 1.65302 3.98726 1.52413 4.76729 1.48697C5.6106 1.44979 5.86247 1.4403 7.99901 1.4403C10.1375 1.4403 10.3874 1.44979 11.2307 1.48697C12.0108 1.52413 12.4362 1.65302 12.7177 1.76333C13.0909 1.90881 13.3578 2.08239 13.6361 2.36189C13.9176 2.64181 14.0912 2.90711 14.2367 3.27993C14.345 3.56382 14.4755 3.98725 14.511 4.76729C14.5502 5.6106 14.5597 5.86247 14.5597 8.00099C14.5597 10.1352 14.5502 10.389 14.511 11.2323ZM15.9514 4.70167C15.9122 3.84925 15.7778 3.26688 15.5781 2.75965C15.3749 2.23341 15.1025 1.78744 14.6565 1.34147C14.2126 0.897484 13.7666 0.625069 13.2403 0.419489C12.7311 0.221797 12.1507 0.0857811 11.2979 0.0486269C10.4451 0.00749969 10.1727 -3.8147e-06 7.99901 -3.8147e-06C5.82727 -3.8147e-06 5.5529 0.00749969 4.7001 0.0486269C3.84926 0.0857811 3.26926 0.221797 2.75766 0.419489C2.23341 0.625069 1.78744 0.897484 1.34346 1.34147C0.897487 1.78744 0.625071 2.23341 0.419877 2.75965C0.222185 3.26688 0.0877708 3.84925 0.0466431 4.70167C0.00948873 5.55447 0 5.82727 0 8.00099C0 10.1727 0.00948873 10.4451 0.0466431 11.2979C0.0877708 12.1488 0.222185 12.7307 0.419877 13.2403C0.625071 13.7646 0.897487 14.2126 1.34346 14.6565C1.78744 15.1005 2.23341 15.3749 2.75766 15.5801C3.26926 15.7778 3.84926 15.9122 4.7001 15.9514C5.5529 15.9905 5.82727 16 7.99901 16C10.1727 16 10.4451 15.9905 11.2979 15.9514C12.1507 15.9122 12.7311 15.7778 13.2403 15.5801C13.7666 15.3749 14.2126 15.1005 14.6565 14.6565C15.1025 14.2126 15.3749 13.7646 15.5781 13.2403C15.7778 12.7307 15.9122 12.1488 15.9514 11.2979C15.9905 10.4451 16 10.1727 16 8.00099C16 5.82727 15.9905 5.55447 15.9514 4.70167Z"
            />
            <path
              fill="white"
              d="M7.99922 10.6654C6.52727 10.6654 5.33288 9.47294 5.33288 8.001C5.33288 6.52669 6.52727 5.33271 7.99922 5.33271C9.47157 5.33271 10.6675 6.52669 10.6675 8.001C10.6675 9.47294 9.47157 10.6654 7.99922 10.6654ZM7.99922 3.89042C5.73022 3.89042 3.89258 5.732 3.89258 8.001C3.89258 10.268 5.73022 12.1077 7.99922 12.1077C10.2682 12.1077 12.1078 10.268 12.1078 8.001C12.1078 5.732 10.2682 3.89042 7.99922 3.89042Z"
            />
          </svg>
          <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.88)' }}>
            {title}
          </Typography.Text>
        </div>

        <div
          style={{
            flexGrow: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {isSmall ? (
            <Typography.Text
              style={{
                fontSize: 14,
                display: 'block',
                textAlign: 'left',
                color: 'rgba(255, 255, 255, 0.88)',
              }}
            >
              Connect your{' '}
              <Typography.Text
                style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.88)' }}
                strong
              >
                Instagram account
              </Typography.Text>
            </Typography.Text>
          ) : (
            <Typography.Text
              style={{
                fontSize: 14,
                display: 'block',
                textAlign: 'left',
                color: 'rgba(255, 255, 255, 0.88)',
              }}
            >
              Connect your{' '}
              <Typography.Text
                style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.88)' }}
                strong
              >
                Instagram
              </Typography.Text>{' '}
              account to access and
              <br />
              display up-to-date, accurate data.
            </Typography.Text>
          )}
          <ConnectedInstagram
            block
            size="large"
            transparent
            label={
              <Typography.Text
                strong
                style={{ fontSize: isSmall ? 12 : 14, color: 'inherit' }}
              >
                Connect Instagram
              </Typography.Text>
            }
          />
        </div>
      </div>
    </div>
  );
};
